import { BookmarkSquareIcon, PencilSquareIcon } from '@heroicons/react/24/outline';
import { MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import { DateTime } from 'luxon';
import { useEffect, useState } from 'react';
import { error, information } from 'src/constants/messages';
import { tenantText } from 'src/constants/texts';
import { TenantListData } from 'src/data-type/data-types';
import { tenantData } from 'src/service/tenant-data';

const TenantList = () => {
  const [tenants, setTenants] = useState<TenantListData>([]);
  const [dispTenants, setDispTenants] = useState<TenantListData>([]);
  const [editingId, setEditingId] = useState<number | null>(null);

  // 更新処理をキャンセルした場合に、画面上のデータを元の状態に戻すときに使用する
  const [searchVal, setSearchVal] = useState('');

  const searchData = (value: string) => {
    if (value === '') setDispTenants(tenants);

    const result: TenantListData = Object.fromEntries(
      Object.entries(tenants).filter(([id, tenant]) => {
        // 1行のデータをすべて結合した文字列に対して、検索を行う
        const searchTarget = `${id}${Object.values(tenant).join(',')}`;
        if (searchTarget.includes(value)) return true;
        return false;
      })
    );

    setDispTenants(result);
  };

  const switchEditMode = (id: number) => {
    setEditingId(id);
  };

  const isValid = () => {
    const name = dispTenants[editingId as number].name;
    if (name === '') {
      window.alert(error.required);
      return false;
    }

    if (name.length > 70) {
      window.alert(error.exceeded);
      return false;
    }

    return true;
  };

  const updateData = async () => {
    if (!isValid()) return;

    const isUpdate = window.confirm(`${editingId}${information.confirm}`);
    if (isUpdate) {
      const id = editingId as number;
      const res = await tenantData.update({ id: id, ...dispTenants[id] });

      if (res === undefined) return;

      if (res.status === 200) {
        // 登録成功時は、DBとtenantsが同値であると仮定し、DB取得を行わずにこのComponentで持つOriginalのテナントリストを更新する
        setTenants((prev) => {
          const newVal = { ...prev };
          newVal[id] = { ...newVal[id], ...dispTenants[id] };
          return newVal;
        });
        window.alert(information.successRegister);
      } else {
        window.alert(error.failedRegister);
        console.log(res.data);
      }
    } else searchData(searchVal);
    // 編集モードを戻す
    setEditingId(null);
  };

  const onChange = (id: number, key: keyof TenantListData[keyof TenantListData], value: string) => {
    setDispTenants((prev) => {
      const newVal = { ...prev };
      newVal[id] = { ...newVal[id], ...{ [key]: value } };
      return newVal;
    });
  };

  useEffect(() => {
    const getTenantList = async () => {
      const res = await tenantData.list();
      if (res === undefined) return;

      if (res.status === 200) {
        setTenants(res.data);
        setDispTenants(res.data);
      }
    };
    getTenantList();
  }, []);

  const thTdStyle = 'px-1 border border-gray-400';

  return (
    <>
      <div className="relative mt-1 w-80">
        <input
          value={searchVal}
          onChange={(e) => {
            setSearchVal(e.target.value);
            searchData(e.target.value);
          }}
          disabled={editingId !== null}
          className="h-8 w-full border pr-8 outline-none"
          data-cy="search"
        />
        <button>
          <MagnifyingGlassIcon className="absolute right-1 top-1 h-6 w-6" />
        </button>
      </div>
      <div className="h-[45rem] overflow-y-auto">
        <table className="mt-8 shadow-lg">
          <thead>
            <tr className="h-16 bg-sky-200 text-center">
              <th className={`w-28 ${thTdStyle}`}>{tenantText.id}</th>
              <th className={`w-72 ${thTdStyle}`}>{tenantText.name}</th>
              <th className={`w-40 ${thTdStyle}`}>{tenantText.dakokuPassword}</th>
              <th className={`w-40 ${thTdStyle}`}>{tenantText.apiPassword}</th>
              <th className={`w-36 ${thTdStyle}`}>{tenantText.expirationDate}</th>
              <th className={`w-12 ${thTdStyle}`}></th>
            </tr>
          </thead>

          {Object.keys(dispTenants).length === 0 && 'now loading...'}

          <tbody data-cy="data-body">
            {Object.entries(dispTenants).map(([id, tenant]) => (
              <tr
                key={id}
                className={`h-12 odd:bg-white even:bg-slate-100 ${
                  editingId === Number(id) && 'border-2 border-green-400'
                }`}
                data-cy={`tr-${id}`}
              >
                <td className={`text-right ${thTdStyle}`}>{id}</td>
                <td title={tenant.name} className={`${thTdStyle}`}>
                  <input
                    value={tenant.name}
                    onChange={(e) => onChange(Number(id), 'name', e.target.value)}
                    readOnly={editingId !== Number(id)}
                    className="w-full bg-transparent outline-none"
                    data-cy={`td-name-${id}`}
                  />
                </td>
                <td className={`${thTdStyle}`}>{tenant.dakokuPassword}</td>
                <td className={`${thTdStyle}`}>{tenant.apiPassword}</td>
                <td className={`${thTdStyle}`}>
                  <input
                    value={tenant.expirationDate ? DateTime.fromISO(tenant.expirationDate).toISODate() ?? '' : ''}
                    onChange={(e) => onChange(Number(id), 'expirationDate', e.target.value)}
                    readOnly={editingId !== Number(id)}
                    type={editingId === Number(id) ? 'date' : 'text'}
                    className="w-full bg-transparent text-center outline-none"
                    data-cy={`td-date-${id}`}
                  />
                </td>
                <td title={`${id}を編集します`} className={`${thTdStyle}`}>
                  {editingId === null && (
                    <PencilSquareIcon
                      className="m-auto h-6 w-6 cursor-pointer"
                      onClick={() => switchEditMode(Number(id))}
                      data-cy={`td-edit-${id}`}
                    />
                  )}
                  {editingId === Number(id) && (
                    <BookmarkSquareIcon
                      className="m-auto h-6 w-6 cursor-pointer"
                      onClick={() => updateData()}
                      data-cy={`td-save-${id}`}
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default TenantList;
